// Assistant API Service
const API_ENDPOINT = import.meta.env.VITE_API_ENDPOINT;

// Get auth token from localStorage
const getAuthToken = () => {
  const tokens = localStorage.getItem('tokens');
  if (tokens) {
    const parsed = JSON.parse(tokens);
    return parsed.idToken;
  }
  return null;
};

// Create assistant with CSV file upload
export const createAssistant = async (name, file) => {
  const token = getAuthToken();

  if (!token) {
    throw new Error('No authentication token found');
  }

  // Create FormData for multipart/form-data upload
  const formData = new FormData();
  formData.append('name', name);
  formData.append('file', file);

  const response = await fetch(`${API_ENDPOINT}/assistants`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create assistant');
  }

  return await response.json();
};

// Get all assistants for the current user
export const getAssistants = async () => {
  const token = getAuthToken();

  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(`${API_ENDPOINT}/assistants`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get assistants');
  }

  return await response.json();
};

// Get single assistant by ID
export const getAssistant = async (assistantId) => {
  const token = getAuthToken();

  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(`${API_ENDPOINT}/assistants/${assistantId}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get assistant');
  }

  return await response.json();
};

// Delete assistant
export const deleteAssistant = async (assistantId) => {
  const token = getAuthToken();

  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(`${API_ENDPOINT}/assistants/${assistantId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete assistant');
  }

  return await response.json();
};

// Toggle assistant status (active/inactive)
export const toggleAssistantStatus = async (assistantId) => {
  const token = getAuthToken();

  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(`${API_ENDPOINT}/assistants/${assistantId}/status`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to toggle status');
  }

  return await response.json();
};

// Get CSV data for an assistant
export const getAssistantData = async (assistantId) => {
  const token = getAuthToken();

  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(`${API_ENDPOINT}/assistants/${assistantId}/data`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get assistant data');
  }

  return await response.json();
};

// Update a cell in the CSV data
export const updateAssistantData = async (assistantId, rowId, columnName, value) => {
  const token = getAuthToken();

  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(`${API_ENDPOINT}/assistants/${assistantId}/data`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ rowId, columnName, value }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update cell');
  }

  return await response.json();
};
